import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@arcadeai/design-system/components/ui/atoms/table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  LayoutGrid,
  List,
  Search,
  Settings2,
} from "lucide-react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { ResourceContainerSkeleton } from "@/components/custom/resources/resource-container-skeleton";

const sections = [
  { binding: "project" as const, resourceType: "user sources" },
  { binding: "tenant" as const, resourceType: "user sources" },
];

export const UserSourcesListSkeleton = () => (
  <ListLayout
    description="Configure OpenID Connect identity providers for end-user authentication on your MCP gateways."
    filters={
      <div className="flex w-full items-center gap-1.5">
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                disabled
                placeholder="Search user sources..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                className="h-9 gap-1.5 border-dashed"
                disabled
                size="sm"
                variant="outline"
              >
                <Settings2 className="size-4" />
                Columns
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-md border p-1">
            <Button className="h-7 w-7" disabled size="icon" variant="ghost">
              <LayoutGrid className="size-4" />
            </Button>
            <Button className="h-7 w-7" disabled size="icon" variant="ghost">
              <List className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    }
    title="User sources"
  >
    <ResourceContainerSkeleton
      sections={sections}
      tableSkeleton={<UserSourcesTableSkeleton />}
    />
  </ListLayout>
);

const UserSourcesTableSkeleton = () => (
  <div className="space-y-4">
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {[
              "Name",
              "Description",
              "Issuer",
              "Client ID",
              "Status",
              "Created",
              "Actions",
            ].map((label) => (
              <TableHead key={label}>
                <div className="flex items-center space-x-2">
                  <span>{label}</span>
                  {label !== "Actions" && (
                    <ChevronsUpDown className="ml-2 h-4 w-4" />
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 2 }, () => (
            <UserSourceRowSkeleton key={crypto.randomUUID()} />
          ))}
        </TableBody>
      </Table>
    </div>

    <div className="flex items-center justify-between px-2">
      <div className="flex items-center gap-2">
        <p className="font-medium text-sm">Rows per page</p>
        <Skeleton className="h-8 w-[70px]" />
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center justify-center font-medium text-sm">
          <span className="flex items-center gap-1">
            Page 1 of <Skeleton className="inline-block h-4 w-6" />
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            className="hidden h-8 w-8 p-0 lg:flex"
            disabled
            variant="outline"
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button className="h-8 w-8 p-0" disabled variant="outline">
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button className="h-8 w-8 p-0" disabled variant="outline">
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            className="hidden h-8 w-8 p-0 lg:flex"
            disabled
            variant="outline"
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const UserSourceRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Skeleton className="h-4 w-28" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-40" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-36" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-24" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-6 w-16 rounded-full" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-20" />
    </TableCell>
    <TableCell>
      <div className="flex gap-1">
        <Skeleton className="size-8" />
        <Skeleton className="size-8" />
      </div>
    </TableCell>
  </TableRow>
);
